import { useCallback, useEffect, useRef, useState } from 'react';
import { billingRequest, billingError, billingUiEnabled, getBillingSession, BILLING_UPDATED_EVENT, usableBalance } from './billingClient';

function validSession(session) {
  return Boolean(session?.token && Number.isFinite(session.expiresAt) && session.expiresAt > Date.now());
}

export default function useBillingWallet() {
  const [state, setState] = useState({ data: null, loading: true, error: '', requiresLogin: false, token: '' });
  const current = useRef({ mounted: false, generation: 0, token: '', running: false, again: false, expiryTimer: null });

  const refresh = useCallback(async () => {
    const requestState = current.current;
    const session = getBillingSession();
    const token = session?.token || '';
    if (!requestState.mounted) return;
    clearTimeout(requestState.expiryTimer);
    if (!billingUiEnabled || !validSession(session)) {
      requestState.generation += 1;
      requestState.token = token;
      requestState.running = false;
      requestState.again = false;
      setState({ data: billingUiEnabled ? null : { enabled: false }, loading: false, error: '', requiresLogin: billingUiEnabled, token });
      return;
    }
    requestState.expiryTimer = setTimeout(refresh, Math.min(Number(session.expiresAt) - Date.now() + 1, 2147483647));
    if (requestState.running && requestState.token === token) {
      requestState.again = true;
      return;
    }
    const generation = ++requestState.generation;
    const previousToken = requestState.token;
    requestState.token = token;
    requestState.running = true;
    requestState.again = false;
    setState((previous) => ({ data: previousToken === token ? previous.data : null, loading: true, error: '', requiresLogin: false, token }));
    const isCurrent = () => requestState.mounted && requestState.generation === generation && getBillingSession()?.token === token && validSession(getBillingSession());
    try {
      const data = await billingRequest('wallet');
      if (!isCurrent()) return;
      if (data?.enabled !== false && usableBalance(data) === null) throw new Error('잔액을 확인하지 못했습니다. 다시 시도해 주세요.');
      setState({ data, loading: false, error: '', requiresLogin: false, token });
    } catch (error) {
      if (isCurrent()) setState({ data: null, loading: false, error: billingError(error), requiresLogin: error.response?.status === 401 || error.code === 'BILLING_LOGIN_REQUIRED', token });
    } finally {
      if (requestState.generation === generation) {
        requestState.running = false;
        if (requestState.mounted && (requestState.again || getBillingSession()?.token !== token || !validSession(getBillingSession()))) refresh();
      }
    }
  }, []);

  useEffect(() => {
    const requestState = current.current;
    requestState.mounted = true;
    const visible = () => { if (document.visibilityState === 'visible') refresh(); };
    const storage = (event) => { if (!event.key || event.key === 'kuesbTeacherBillingSession') refresh(); };
    window.addEventListener(BILLING_UPDATED_EVENT, refresh);
    window.addEventListener('focus', refresh);
    window.addEventListener('pageshow', refresh);
    window.addEventListener('storage', storage);
    document.addEventListener('visibilitychange', visible);
    refresh();
    return () => {
      requestState.mounted = false;
      requestState.generation += 1;
      requestState.running = false;
      clearTimeout(requestState.expiryTimer);
      window.removeEventListener(BILLING_UPDATED_EVENT, refresh);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('pageshow', refresh);
      window.removeEventListener('storage', storage);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [refresh]);

  const session = getBillingSession();
  const sameMember = state.token === (session?.token || '');
  const requiresLogin = billingUiEnabled && (!validSession(session) || (sameMember && state.requiresLogin));
  const data = sameMember && !requiresLogin ? state.data : null;
  return { data, loading: !requiresLogin && (state.loading || !sameMember), error: sameMember ? state.error : '', requiresLogin, refresh, balance: usableBalance(data) };
}
